import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

/**
 * Status chips. Tone is semantic, not decorative — `negative` means money
 * lost / record failed, `positive` means settled / paid / healthy. Screens
 * should map a domain status to a tone once, in a lookup near the screen, so
 * the same status never renders two different colours in two places.
 */
export type Tone = 'neutral' | 'signal' | 'positive' | 'negative' | 'warn' | 'info';

const TONES: Record<Tone, string> = {
  neutral: 'bg-neutral-chip text-ink-muted',
  signal: 'bg-signal-muted text-signal-ink',
  positive: 'bg-positive-muted text-positive',
  negative: 'bg-negative-muted text-negative',
  warn: 'bg-warn-muted text-warn',
  info: 'bg-info-muted text-info',
};

const DOTS: Record<Tone, string> = {
  neutral: 'bg-ink-faint',
  signal: 'bg-signal',
  positive: 'bg-positive',
  negative: 'bg-negative',
  warn: 'bg-warn',
  info: 'bg-info',
};

export function Badge({
  tone = 'neutral',
  dot = false,
  pulse = false,
  className,
  children,
}: {
  tone?: Tone;
  /** Leading status dot — use on lifecycle states, not on counts. */
  dot?: boolean;
  /** Animate the dot. Reserved for genuinely live state (a running shift). */
  pulse?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 rounded-xs px-1.5 py-0.5',
        'text-2xs leading-4 font-medium tracking-wide whitespace-nowrap',
        TONES[tone],
        className
      )}
    >
      {dot ? (
        <span
          className={cn(
            'size-1.5 shrink-0 rounded-full',
            DOTS[tone],
            pulse && 'animate-[pulse-signal_1.8s_ease-in-out_infinite]'
          )}
          aria-hidden
        />
      ) : null}
      {children}
    </span>
  );
}

/** Monospace chip for identifiers: SKU, barcode, PO number, register id. */
export function Code({
  children,
  className,
  copyable = false,
}: {
  children: ReactNode;
  className?: string;
  copyable?: boolean;
}) {
  return (
    <span
      className={cn(
        'bg-surface-sunken text-2xs text-ink-muted rounded-xs px-1 py-px font-mono',
        copyable && 'hover:text-ink cursor-copy',
        className
      )}
      onClick={
        copyable
          ? (e) => {
              e.stopPropagation();
              void navigator.clipboard?.writeText(String(children));
            }
          : undefined
      }
      title={copyable ? 'Click to copy' : undefined}
    >
      {children}
    </span>
  );
}

/** A count pill for nav items and tabs. Zero renders nothing at all —
 *  an empty queue should be silent, not a grey "0". */
export function Count({ value, tone = 'neutral' }: { value: number; tone?: Tone }) {
  if (!value) return null;
  return (
    <span
      className={cn(
        'ml-auto inline-flex h-4 min-w-4 items-center justify-center rounded-xs px-1',
        'text-2xs font-mono tabular-nums',
        TONES[tone]
      )}
    >
      {value > 999 ? '999+' : value}
    </span>
  );
}
